import type { SupabaseClient } from "@supabase/supabase-js"

type ResourceSeed = {
  title: string
  url: string
  category: string
  description: string
}

export type DefaultResourceRow = {
  id: string
  destination_code: string | null
  nationality_code: string | null
  title: string
  url: string
  category: string
  description: string | null
  sort_order: number
  active: boolean
}

/** Used when `default_resources` is empty or the query fails (must match DB seed). */
export const UNIVERSAL_FALLBACK_SEEDS: ReadonlyArray<ResourceSeed> = [
  {
    title: "IATA Travel Centre",
    url: "https://www.iatatravelcentre.com",
    category: "visa",
    description: "Third-party aggregator for visa and entry requirement checks (not a government source)",
  },
  {
    title: "Sherpa Travel Requirements",
    url: "https://apply.joinsherpa.com",
    category: "visa",
    description: "Third-party travel-requirements checker (informational; confirm with official sources)",
  },
]

/**
 * Whether a template row applies to a workspace route (mirrors the SQL filter in
 * {@link fetchMatchingDefaultResources}).
 */
export function rowAppliesToWorkspace(
  row: { destination_code: string | null; nationality_code: string | null; active: boolean },
  destinationCode: string | null,
  nationalityCode: string | null
): boolean {
  if (!row.active) return false
  const d = destinationCode?.trim() ? destinationCode.trim().toUpperCase() : null
  const n = nationalityCode?.trim() ? nationalityCode.trim().toUpperCase() : null
  const rd = row.destination_code?.trim() ? row.destination_code.trim().toUpperCase() : null
  const rn = row.nationality_code?.trim() ? row.nationality_code.trim().toUpperCase() : null
  const destOk = rd === null || rd === d
  const natOk = rn === null || rn === n
  return destOk && natOk && d != null && n != null
}

function normalizeResourceUrlKey(raw: string): string {
  const t = raw.trim().toLowerCase()
  try {
    const u = new URL(t.startsWith("http") ? t : `https://${t}`)
    u.hash = ""
    const path = u.pathname.replace(/\/+$/, "") || ""
    return `${u.hostname}${path}`
  } catch {
    return t.replace(/\/+$/, "")
  }
}

export function dedupeResourceSeedsByUrl(seeds: ResourceSeed[]): ResourceSeed[] {
  const seen = new Set<string>()
  const out: ResourceSeed[] = []
  for (const s of seeds) {
    const k = normalizeResourceUrlKey(s.url)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(s)
  }
  return out
}

async function fetchMatchingDefaultResources(
  client: SupabaseClient,
  destinationCode: string,
  nationalityCode: string
): Promise<DefaultResourceRow[]> {
  const dest = destinationCode.trim().toUpperCase()
  const nat = nationalityCode.trim().toUpperCase()

  const { data, error } = await client
    .from("default_resources")
    .select("id, destination_code, nationality_code, title, url, category, description, sort_order, active")
    .eq("active", true)
    .or(`destination_code.eq.${dest},destination_code.is.null`)
    .or(`nationality_code.eq.${nat},nationality_code.is.null`)
    .order("destination_code", { ascending: true, nullsFirst: false })
    .order("sort_order", { ascending: true })

  if (error) {
    console.warn("[populate-resources] default_resources query failed:", error.message)
    return []
  }

  return (data ?? []) as DefaultResourceRow[]
}

function rowsToSeeds(rows: DefaultResourceRow[]): ResourceSeed[] {
  return rows.map((r) => ({
    title: r.title,
    url: r.url,
    category: r.category,
    description: (r.description ?? "").trim() || r.title,
  }))
}

async function loadExistingUrlKeys(
  client: SupabaseClient,
  workspaceId: string
): Promise<Set<string>> {
  const { data, error } = await client.from("workspace_resources").select("url").eq("workspace_id", workspaceId)
  if (error) {
    return new Set()
  }
  return new Set((data ?? []).map((r) => normalizeResourceUrlKey(String((r as { url?: string }).url ?? ""))))
}

/**
 * @deprecated Use {@link UNIVERSAL_FALLBACK_SEEDS} or DB-driven provisioning; kept for tests that assert universal links.
 */
export function buildDefaultResourceSeeds(destinationCode: string | null): ResourceSeed[] {
  void destinationCode
  return [...UNIVERSAL_FALLBACK_SEEDS]
}

/**
 * Inserts default resources from `default_resources` for the workspace nationality + destination.
 * Falls back to IATA + Sherpa only when no rows match or the table is unavailable.
 */
export async function populateDefaultResources(
  workspaceId: string,
  destinationCode: string | null,
  nationalityCode: string | null,
  client: SupabaseClient
): Promise<void> {
  const dest = destinationCode?.trim() ? destinationCode.trim().toUpperCase() : null
  const nat = nationalityCode?.trim() ? nationalityCode.trim().toUpperCase() : null
  if (!dest || !nat) {
    return
  }

  let rows = await fetchMatchingDefaultResources(client, dest, nat)
  if (rows.length === 0) {
    rows = []
  }

  let seeds = rowsToSeeds(rows)
  if (seeds.length === 0) {
    seeds = [...UNIVERSAL_FALLBACK_SEEDS]
  }
  seeds = dedupeResourceSeedsByUrl(seeds)

  const existing = await loadExistingUrlKeys(client, workspaceId)

  for (const seed of seeds) {
    const key = normalizeResourceUrlKey(seed.url)
    if (existing.has(key)) continue

    const { error } = await client.from("workspace_resources").insert({
      workspace_id: workspaceId,
      title: seed.title,
      url: seed.url,
      category: seed.category,
      description: seed.description,
      is_favorite: false,
    })
    if (error) {
      continue
    }
    existing.add(key)
  }
}
